import type { ParsedRow } from '../components/Evenementiel/convives/useConvivesActions';

/**
 * Client du Portail Organisateur (refonte design lot 10) — public, sur le lien /organisateur/:token.
 *
 * Il ne passe PAS par `apiFetch` : celui-ci jette une erreur dès qu'une réponse n'est pas 2xx et n'en
 * garde que le message, alors que le portail vit précisément de ces réponses-là — « code refusé, il
 * reste 3 tentatives », « bloqué 15 minutes », « accès expiré », « plafond atteint » (maquette 19e). Ici,
 * toute réponse JSON revient telle quelle avec son statut ; c'est l'écran qui décide quoi en dire.
 * La session de 12 h (jeton renvoyé par « ouvrir ») part dans un en-tête dédié, jamais dans l'URL.
 */
export const EN_TETE_SESSION = 'X-Organisateur-Session';
const CLE_SESSION = (token: string) => `orga-session:${token}`;

export interface ReponseOrga<T> { status: number; ok: boolean; data: T & { etat?: string; error?: string } }

export const lireSession = (token: string): string => { try { return localStorage.getItem(CLE_SESSION(token)) || ''; } catch { return ''; } };
export const ecrireSession = (token: string, session: string) => { try { if (session) localStorage.setItem(CLE_SESSION(token), session); else localStorage.removeItem(CLE_SESSION(token)); } catch { /* stockage indisponible : la session ne survit pas au rechargement, c'est tout */ } };

async function appel<T = any>(token: string, chemin: string, options: RequestInit = {}): Promise<ReponseOrga<T>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...((options.headers as Record<string, string>) || {}) };
    const session = lireSession(token);
    if (session) headers[EN_TETE_SESSION] = session;
    try {
        const res = await fetch(`/api/organisateur/${encodeURIComponent(token)}${chemin}`, { ...options, headers });
        let data: any = {};
        try { data = await res.json(); } catch { data = { error: 'Réponse illisible' }; }
        return { status: res.status, ok: res.ok, data };
    } catch {
        return { status: 0, ok: false, data: { etat: 'hors_service', error: 'Le service est momentanément indisponible.' } as any };
    }
}

export interface PorteInfo {
    etat: 'ok' | 'expire' | 'revoque' | 'inconnu';
    session_ouverte?: boolean;
    expires_at?: string | null;
    bloque_secondes?: number;
    organisateur?: { name: string };
    etablissement?: { name: string; logo_url: string | null; theme_color: string | null };
    dossier?: { label: string; date: string; date_label: string; couverts: number };
}

export interface ConviveOrga { id: string; name: string; email: string; invitation_sent_at: string | null; responded_at: string | null; choix: string }

export interface PortailInfo extends PorteInfo {
    cloture: { date: string | null; closed_at: string | null; locked_at: string | null };
    menu_pret: boolean;
    menu_label: string;
    compteurs: { importes: number; invites: number; reponses: number; silencieux: number; non_invites: number };
    gestes: { import_le: string | null; envoi_le: string | null; relance_le: string | null; relance_possible_le: string | null };
    plafond: { max: number; utilises: number; restants: number };
    contraintes: number;
    mot_accueil: string;
    convives: ConviveOrga[];
}

export const organisateurApi = {
    porte: (token: string) => appel<PorteInfo>(token, ''),
    ouvrir: (token: string, code: string) => appel<{ session?: string; session_expires_at?: string; tentatives_restantes?: number; retry_after_sec?: number }>(token, '/ouvrir', { method: 'POST', body: JSON.stringify({ code }) }),
    portail: (token: string) => appel<PortailInfo>(token, '/portail'),
    importer: (token: string, rows: ParsedRow[], fileName: string) => appel<{ imported: number; duplicates_skipped: number; invalid_skipped: number }>(token, '/convives/import', { method: 'POST', body: JSON.stringify({ rows, file_name: fileName }) }),
    envoyer: (token: string, guestIds: string[], mode: 'envoi' | 'relance') => appel<{ sent: number; failed: number; last_error?: string; restants?: number }>(token, '/convives/envoyer', { method: 'POST', body: JSON.stringify({ guest_ids: guestIds, mode }) }),
    retirer: (token: string, guestId: string) => appel<{ success?: boolean; responded_at?: string }>(token, `/convives/${guestId}`, { method: 'DELETE' }),
    signalerAbsence: (token: string, guestId: string) => appel<{ success?: boolean; email_sent?: boolean }>(token, `/convives/${guestId}/absence`, { method: 'POST', body: '{}' }),
    motAccueil: (token: string, text: string) => appel<{ mot_accueil: string }>(token, '/mot-accueil', { method: 'PUT', body: JSON.stringify({ text }) }),
    deconnexion: (token: string) => appel(token, '/deconnexion', { method: 'POST', body: '{}' }),
};
